import * as React from "react";
import { Avatar, Box, Chip, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { diffChars } from "diff";
import ReactMarkdown from "react-markdown";

import { Conversation } from "@/hooks/api/use-call-logs";
import { ColorScheme } from "@/styles/theme/types";

import { CustomSkeleton } from "../customSkeleton/custom-skeleton";

dayjs.extend(utc);

/**
 * The message bubbles a conversation is read through.
 *
 * Extracted from the call log modal so the Proving Ground can show a simulated call in the
 * same viewer as a real one. They are the same thing - a conversation the agent ran - and
 * rendering them through two implementations is how the two screens drift apart.
 */

export function ReactMarkdownRenderer({ content }: { content: string }) {
	if (!content) return null;

	const newContent = content.replaceAll("\n\n", "![alt text](image.jpg)");

	return (
		<ReactMarkdown
			components={{
				img: () => <div style={{ width: "100%", height: "8px" }} />,
				p: ({ children }) => <span style={{ whiteSpace: "pre-wrap" }}>{children}</span>,
				strong: ({ children }) => <strong>{children}</strong>,
				em: ({ children }) => <em>{children}</em>,
				code: ({ children }) => (
					<code
						style={{
							backgroundColor: "rgba(0, 0, 0, 0.1)",
							padding: "2px 4px",
							borderRadius: "3px",
							fontSize: "0.85em",
						}}
					>
						{children}
					</code>
				),
				pre: ({ children }) => (
					<pre
						style={{
							backgroundColor: "rgba(0, 0, 0, 0.05)",
							padding: "8px",
							borderRadius: "4px",
							overflow: "auto",
							fontSize: "0.85em",
							margin: "4px 0",
						}}
					>
						{children}
					</pre>
				),
				ul: ({ children }) => <ul style={{ margin: "4px 0", paddingLeft: "16px" }}>{children}</ul>,
				ol: ({ children }) => <ol style={{ margin: "4px 0", paddingLeft: "16px" }}>{children}</ol>,
				li: ({ children }) => <li style={{ margin: "2px 0" }}>{children}</li>,
				blockquote: ({ children }) => (
					<blockquote
						style={{
							borderLeft: "3px solid #ccc",
							margin: "4px 0",
							paddingLeft: "8px",
							fontStyle: "italic",
						}}
					>
						{children}
					</blockquote>
				),
			}}
		>
			{newContent}
		</ReactMarkdown>
	);
}

export function HighlightedDiffRenderer({
	completion,
	content,
	colorScheme,
}: {
	completion: string;
	content: string;
	colorScheme: ColorScheme;
}) {
	const diffs = diffChars(content || "", completion);

	const DIFF_MARKER = "⟦DIFF_HIGHLIGHT⟧";

	const markdownContent = diffs
		.map((part) => {
			if (part.added) {
				return `${DIFF_MARKER}${part.value}${DIFF_MARKER}`;
			}
			return part.value;
		})
		.join("");

	const parts = markdownContent.split(DIFF_MARKER);
	const processedParts: React.ReactNode[] = [];

	for (const [index, part] of parts.entries()) {
		if (index % 2 === 1) {
			processedParts.push(
				<Box
					key={`diff-${index}`}
					component="span"
					sx={{
						backgroundColor: colorScheme === "dark" ? "rgba(221, 187, 49, 0.4)" : "rgba(221, 187, 49, 0.4)",
						padding: content.length > 0 ? "0px 4px" : "0px",
						whiteSpace: "pre-wrap",
					}}
				>
					<ReactMarkdownRenderer content={part} />
				</Box>
			);
		} else if (part) {
			processedParts.push(
				<Box key={`unchanged-${index}`} component="span" sx={{ whiteSpace: "pre-wrap" }}>
					<ReactMarkdownRenderer content={part} />
				</Box>
			);
		}
	}

	return processedParts;
}

export function MessagesPlaceholder({
	isAgent,
	messageWidth = 320,
}: {
	isAgent?: boolean;
	messageWidth?: number;
}): React.JSX.Element {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: isAgent ? "row" : "row-reverse",
				mb: 1.75,
				gap: 1,
				alignItems: "flex-start",
			}}
		>
			<CustomSkeleton variant="rounded" sx={{ width: 32, height: 32, borderRadius: 2 }} />

			<Box
				sx={{
					maxWidth: "70%",
					display: "flex",
					flexDirection: "column",
					alignItems: isAgent ? "flex-start" : "flex-end",
					gap: 0.5,
				}}
			>
				<Box sx={{ display: "flex", flexDirection: isAgent ? "row" : "row-reverse", alignItems: "center", gap: 1 }}>
					{isAgent && <CustomSkeleton variant="rounded" sx={{ width: 48, height: 16 }} />}
					<CustomSkeleton variant="rounded" sx={{ width: 50, height: 16 }} />
					{isAgent && <CustomSkeleton variant="rounded" sx={{ width: 28, height: 18 }} />}
				</Box>
				<CustomSkeleton variant="rounded" sx={{ width: messageWidth, height: 50, borderRadius: 2, mt: 0.5 }} />
			</Box>
		</Box>
	);
}

export function MessageBubble({
	message,
	colorScheme,
	currentTime,
	currentTimeRef,
	plain = false,
}: {
	message: Conversation;
	colorScheme: ColorScheme;
	currentTime: number;
	currentTimeRef: React.MutableRefObject<number>;
	/** Reading mode for a business owner: the words, without the timing telemetry beside them. */
	plain?: boolean;
}) {
	const isAgent = message?.role === "assistant";
	const isUser = message?.role === "user";
	const isSystem = !isAgent && !isUser;
	const messageRef = React.useRef<HTMLDivElement>(null);

	const completion = (message as any).completion?.replaceAll(/[\n\s]/g, "") || "";
	const content = message.content?.replaceAll(/[\n\s]/g, "") || "";

	// The diff between what was generated and what was spoken is an engineering read, not a business one.
	const showDiff = !plain && completion && completion !== content;

	const start = message.metadata?.start;
	const end = message.metadata?.end;
  const push = message.metadata?.push;

  const startValue = (start / 1000).toFixed(1);
  const endValue = (end / 1000).toFixed(1);

  const pushValue = push && end ? +(push - end).toFixed(1) : push ? +push.toFixed(1) : null;
  
  const timeLabel = start && end ? `${startValue} - ${endValue}` : null;
  const pushLabel = pushValue ? `lag: ${pushValue}` : null;
  const label = timeLabel && pushLabel ? `(${timeLabel} ${pushLabel}ms)` : pushLabel ? `(${pushLabel}ms)` : timeLabel ? `(${timeLabel})` : null;

	const preciseTime = currentTimeRef.current || currentTime;
	const currentTimeMs = preciseTime * 1000;
	const isCurrentMessage = currentTimeMs > 0 && start != null && end != null && currentTimeMs >= start && currentTimeMs <= end;

	return (
		<Box
			ref={messageRef}
			sx={{
				display: "flex",
				flexDirection: isAgent ? "row" : "row-reverse",
				mb: 2,
				alignItems: "flex-start",
				justifyContent: isSystem ? "center" : "inherit",
				transition: "all 0.3s ease-in-out",
        ...(isCurrentMessage && {
          paddingLeft: isAgent ? '10px' : '0px',
          paddingRight: isAgent ? '0px' : '10px',
        })
			}}
		>
			{isSystem ? (
				<Tooltip
					title={JSON.stringify(message, null, 2)}
					arrow
					slotProps={{ tooltip: { sx: { maxWidth: "max-content", whiteSpace: "pre-wrap" } } }}
				>
					<Box
						sx={{
							bgcolor: "var(--mui-palette-info-main)",
							color: "var(--mui-palette-info-contrastText)",
							p: 1.5,
							borderRadius: 2,
							position: "relative",
						}}
					>
						<Typography variant="body2" sx={{ wordBreak: "break-word" }}>
							<ReactMarkdown
								components={{
									p: ({ children }) => <span>{children}</span>,
								}}
							>
								{message.content}
							</ReactMarkdown>
						</Typography>
					</Box>
				</Tooltip>
			) : (
				<>
					<Avatar
						sx={{
							width: 32,
							height: 32,
							mr: isAgent ? 1 : 0,
							ml: isAgent ? 0 : 1,
							bgcolor: isAgent ? "primary.main" : colorScheme === "dark" ? "grey.800" : "grey.200",
							color: isAgent ? "primary.contrastText" : colorScheme === "dark" ? "white" : "black",
							fontSize: "0.75rem",
							fontWeight: "bold",
              ...(isCurrentMessage && {
                border: "1px solid",
                borderColor: "primary.main",
                boxShadow:
                  colorScheme === "dark" ? "0 4px 12px rgba(0, 0, 0, 0.15)" : "0 4px 12px rgba(255, 255, 255, 0.95)",
                outline: "none",
                animation: "pulse-message 2s ease-in-out infinite",
                "@keyframes pulse-message": {
                  "0%, 100%": {
                    borderColor: 'primary.main',
                    boxShadow:
                      colorScheme === "dark" ? "0 4px 12px rgba(0, 0, 0, 0.45)" : "0 4px 12px rgba(255, 255, 255, 0.95)",
                  },
                  "50%": {
                    borderColor: 'primary.dark',
                    boxShadow:
                      colorScheme === "dark"
                        ? "0 4px 20px rgba(25, 118, 210, 0.4)"
                        : "0 4px 20px rgba(99, 109, 222, 0.75)",
                  },
                },
              }),
						}}
					>
						{isAgent ? "AI" : plain ? "C" : "U"}
					</Avatar>
					<Box
						sx={{
							maxWidth: "70%",
							display: "flex",
							flexDirection: "column",
							alignItems: isAgent ? "flex-start" : "flex-end",
							gap: 0.5,
						}}
					>
						<Box sx={{ display: "flex", flexDirection: isAgent ? "row" : "row-reverse", alignItems: "center", gap: 1 }}>
							{isAgent || plain ? (
								<Typography variant="caption" sx={{ fontWeight: 500, fontSize: "12px" }}>
									{plain ? (isAgent ? "Receptionist" : "Customer") : "AI Agent"}
								</Typography>
							) : null}
							<Typography
								variant="caption"
								sx={{
									color: "text.secondary",
									fontSize: "12px",
								}}
							>
								{dayjs(message.createdAt).format(plain ? "HH:mm" : "HH:mm:ss")}
							</Typography>
							{isAgent && !plain && (
								<Chip
									label="AI"
									size="small"
									color="primary"
									sx={{
										fontSize: "10px",
										height: "18px",
									}}
								/>
							)}
							{label && !plain ? (
								<Box sx={{ display: "flex", alignItems: "center", flexDirection: isAgent ? "row" : "row-reverse", gap: 1 }}>
									<Typography variant="caption" sx={{ fontSize: "11px" }}>
										{label}
									</Typography>
									{/* {isCurrentMessage && (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												animation: "pulse-microphone 1.5s ease-in-out infinite",
												"@keyframes pulse-microphone": {
													"0%, 100%": {
														opacity: 1,
														transform: "scale(1)",
													},
													"50%": {
														opacity: 0.6,
														transform: "scale(1.2)",
													},
												},
											}}
										>
											<MicrophoneIcon size={22} weight="fill" color="var(--mui-palette-primary-main)" />
										</Box>
									)} */}
								</Box>
							) : null}
						</Box>
						<Box
							sx={{
								bgcolor: isAgent ? "primary.main" : colorScheme === "dark" ? "grey.800" : "grey.100",
								color: isAgent ? "primary.contrastText" : "text.primary",
								p: 1.25,
								borderRadius: 2,
								position: "relative",
								transition: "all 0.3s ease-in-out",
								border: "1px solid transparent",
								...(isCurrentMessage && {
									border: "1px solid",
									borderColor: "primary.main",
									boxShadow:
										colorScheme === "dark" ? "0 4px 12px rgba(0, 0, 0, 0.15)" : "0 4px 12px rgba(255, 255, 255, 0.95)",
									outline: "none",
									animation: "pulse-message 2s ease-in-out infinite",
									"@keyframes pulse-message": {
										"0%, 100%": {
											borderColor: 'primary.main',
											boxShadow:
												colorScheme === "dark" ? "0 4px 12px rgba(0, 0, 0, 0.45)" : "0 4px 12px rgba(255, 255, 255, 0.95)",
										},
										"50%": {
											borderColor: 'primary.dark',
											boxShadow:
												colorScheme === "dark"
													? "0 4px 20px rgba(25, 118, 210, 0.4)"
													: "0 4px 20px rgba(99, 109, 222, 0.75)",
										},
									},
								}),
							}}
						>
							<Typography variant="body2" sx={{ wordBreak: "break-word" }}>
								{showDiff ? (
									<HighlightedDiffRenderer
										completion={message.completion || ""}
										content={message.content || ""}
										colorScheme={colorScheme}
									/>
								) : (
									<ReactMarkdownRenderer content={message.content} />
								)}
							</Typography>
							{message?.usage?.sentiment && !plain && (
								<Chip
									label={message?.usage?.sentiment}
									size="small"
									sx={{
										position: "absolute",
										top: -8,
										right: 8,
										fontSize: "0.6rem",
										height: 16,
										bgcolor:
											message?.usage?.sentiment === "positive"
												? "success.light"
												: message?.usage?.sentiment === "negative"
													? "error.light"
													: "warning.light",
										color: "white",
									}}
								/>
							)}
						</Box>
					</Box>
				</>
			)}
		</Box>
	);
}
